/**
 * MetricsCharts — the Recharts chart-region wrappers for the Overview cockpit. Four ERP-grade visuals, each
 * always wrapped in a <ResponsiveContainer dir="ltr"> (so the chart never mirrors but the page still RTLs),
 * each token-colored (no raw hex, L-2), each rendering its OWN empty state when the data is all-zero:
 *   • LabGrowthChart      — cumulative lab-growth line over the trend window (monotonic running total).
 *   • DistributionDonut   — a reusable status donut (subscription-status + ticket-status share it).
 *   • CategoryBarChart    — tickets-by-category bars (one token color per category).
 *   • OnboardingFunnel    — a provisioned → synced → activeSub (→ everLoggedIn) horizontal funnel.
 *
 * a11y: each chart sits under a labelled container; the Recharts SVG is decorative (the numbers also live in
 * the KPI tiles + tooltips), so the wrapper carries an aria-label summary. Numerics are LTR.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { ChartDatum } from "../metrics.lib";
import { fmtCount, shortBucket } from "../metrics.lib";
import type { TrendPoint } from "../metrics.types";

const CHART_HEIGHT = 240;

/** Shared empty placeholder for a chart with no data yet (role=status, glyph + text). */
function ChartEmpty({ glyph = "◷" }: { glyph?: string }) {
  const { t } = useT();
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        alignItems: "center",
        justifyContent: "center",
        minHeight: CHART_HEIGHT,
        textAlign: "center",
        color: "var(--nv-slate)",
      }}
    >
      <span aria-hidden style={{ fontSize: "var(--nv-fs-h2)", color: "var(--nv-steel)" }}>
        {glyph}
      </span>
      <strong style={{ color: "var(--nv-ink)" }}>{t("mgmt.overview.chart.empty.title")}</strong>
      <p style={{ margin: 0, maxWidth: "42ch", fontSize: "var(--nv-fs-caption)" }}>
        {t("mgmt.overview.chart.empty.desc")}
      </p>
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: token.surface,
  border: `1px solid ${token.line}`,
  borderRadius: token.rSm,
  color: token.ink,
} as const;

// ── Cumulative lab-growth line ──────────────────────────────────────────────────────

export function LabGrowthChart({
  series,
  bucket,
}: {
  series: ReadonlyArray<TrendPoint>;
  bucket: "day" | "month";
}) {
  const { t } = useT();
  const data = series.map((p) => ({ ...p, label: shortBucket(p.date, bucket) }));
  // The line is genuinely empty only when there are no buckets at all (the running total can legitimately be 0).
  if (data.length === 0) return <ChartEmpty />;

  return (
    <div
      style={{ width: "100%", height: CHART_HEIGHT }}
      dir="ltr"
      role="img"
      aria-label={t("mgmt.overview.chart.labGrowth.aria", { n: data[data.length - 1]?.value ?? 0 })}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} stroke={token.slate} fontSize={12} width={44} />
          <Tooltip
            cursor={{ stroke: token.line }}
            formatter={(v: number | string) => [fmtCount(Number(v)), t("mgmt.overview.chart.labGrowth.tip")]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={token.azure}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Reusable status donut ─────────────────────────────────────────────────────────────

export function DistributionDonut({ data, ariaLabel }: { data: ReadonlyArray<ChartDatum>; ariaLabel: string }) {
  const { t } = useT();
  if (data.length === 0) return <ChartEmpty glyph="◔" />;

  return (
    <div style={{ width: "100%", height: CHART_HEIGHT }} dir="ltr" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data as ChartDatum[]}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} stroke={token.surface} />
            ))}
          </Pie>
          <Tooltip
            formatter={(v: number | string, name: string) => [fmtCount(Number(v)), name]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value: string) => (
              <span style={{ color: token.slate, fontSize: 12 }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <span className="nv-sr-only">{t("mgmt.overview.chart.donut.sr", { n: data.length })}</span>
    </div>
  );
}

// ── Tickets-by-category bars ────────────────────────────────────────────────────────────

export function CategoryBarChart({ data, ariaLabel }: { data: ReadonlyArray<ChartDatum>; ariaLabel: string }) {
  const { t } = useT();
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <ChartEmpty glyph="▭" />;

  return (
    <div style={{ width: "100%", height: CHART_HEIGHT }} dir="ltr" role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data as ChartDatum[]} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={token.lineFaint} vertical={false} />
          <XAxis dataKey="name" stroke={token.slate} fontSize={12} tickLine={false} />
          <YAxis allowDecimals={false} stroke={token.slate} fontSize={12} width={44} />
          <Tooltip
            cursor={{ fill: token.lineFaint }}
            formatter={(v: number | string) => [fmtCount(Number(v)), t("mgmt.overview.chart.byCategory.tip")]}
            contentStyle={TOOLTIP_STYLE}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Onboarding funnel (horizontal stages) ─────────────────────────────────────────────

export interface FunnelStage {
  key: string;
  label: string;
  value: number;
}

/**
 * A lightweight horizontal funnel — each stage is a proportional bar (width = stage / first-stage), so the
 * narrowing is read visually + numerically. Built from divs (not Recharts) because Recharts has no first-
 * class funnel in v2 and the proportional bars read clearer; still token-driven + AA. Renders an empty state
 * when the top stage is 0.
 */
export function OnboardingFunnel({ stages }: { stages: ReadonlyArray<FunnelStage> }) {
  const top = stages[0]?.value ?? 0;
  if (top === 0) return <ChartEmpty glyph="⬡" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
      {stages.map((s, i) => {
        const pct = top === 0 ? 0 : Math.round((s.value / top) * 100);
        return (
          <div key={s.key} style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "var(--nv-s-2)",
                fontSize: "var(--nv-fs-caption)",
                color: "var(--nv-text-2, var(--nv-slate))",
              }}
            >
              <span>{s.label}</span>
              <span className="nv-ltr-island" style={{ fontFamily: "var(--nv-font-mono)", fontWeight: 600 }}>
                {fmtCount(s.value)}
                {i > 0 ? ` · ${pct} %` : ""}
              </span>
            </div>
            <div
              style={{
                height: 14,
                borderRadius: "var(--nv-r-pill)",
                background: "var(--nv-surface-2)",
                overflow: "hidden",
              }}
              role="img"
              aria-label={`${s.label}: ${fmtCount(s.value)} (${pct} %)`}
            >
              <div
                style={{
                  width: `${Math.max(pct, s.value > 0 ? 4 : 0)}%`,
                  height: "100%",
                  background: "var(--nv-azure)",
                  transition: "width var(--nv-base) var(--nv-ease)",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
